import { useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { inr, useLoad } from "@/lib/api";
import { ErrorBox } from "@/components/common";
import MonthlyShare from "@/components/MonthlyShare";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function MonthlyReport() {
  const [year, setYear] = useState(new Date().getFullYear());
  const { data: r, error } = useLoad(`/reports/monthly?year=${year}`, null);
  const chart = r?.months.map((m, i) => ({ name: MONTHS[i], Collections: m.net_collection, Costs: m.expenses + m.lab_costs, Profit: m.profit })) || [];
  const t = r?.totals || {};
  return (
    <div data-testid="monthly-report">
      <div className="toolbar">
        <div className="chips">
          <button className="chip" onClick={() => setYear(year - 1)} data-testid="monthly-prev-year">‹ {year - 1}</button>
          <b className="year-label" data-testid="monthly-year">{year}</b>
          <button className="chip" onClick={() => setYear(year + 1)} data-testid="monthly-next-year">{year + 1} ›</button>
        </div>
      </div>
      <ErrorBox error={error} testid="monthly-error" />
      {r && <>
        <MonthlyShare year={year} report={r} key={year} />
        <section className="summary-grid four">
          <div className="mini-stat"><span>Net collections</span><b className="in" data-testid="monthly-total-collections">{inr(t.net_collection)}</b></div>
          <div className="mini-stat"><span>Expenses</span><b className="out" data-testid="monthly-total-expenses">{inr(t.expenses)}</b></div>
          <div className="mini-stat"><span>Lab costs</span><b className="out" data-testid="monthly-total-lab">{inr(t.lab_costs)}</b></div>
          <div className="mini-stat strong"><span>Profit</span><b data-testid="monthly-total-profit">{inr(t.profit)}</b></div>
        </section>
        <section className="chart-card" data-testid="monthly-chart">
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#f1e9e8" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#806f73" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#806f73" }} axisLine={false} tickLine={false} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
              <Tooltip formatter={(v) => inr(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Collections" fill="#177b74" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Costs" fill="#d99a06" radius={[4, 4, 0, 0]} />
              <Line dataKey="Profit" stroke="#762c39" strokeWidth={2.5} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </section>
        <section className="table-card">
          <div className="table-head month-grid"><span>Month</span><span>Collected</span><span>Refunds</span><span>Expenses</span><span>Lab</span><span>Profit</span></div>
          {r.months.map((m, i) => (
            <div className="table-row month-grid" key={m.month} data-testid={`monthly-row-${m.month}`}>
              <b>{MONTHS[i]} {year}</b><span className="amount">{inr(m.collections)}</span><span className="amount">{inr(m.refunds)}</span>
              <span className="amount">{inr(m.expenses)}</span><span className="amount">{inr(m.lab_costs)}</span>
              <span className={m.profit < 0 ? "amount due" : "amount credit"} data-testid={`monthly-profit-${m.month}`}>{inr(m.profit)}</span>
            </div>
          ))}
        </section>
        {Object.keys(r.expense_categories).length > 0 && <section className="cat-card" data-testid="expense-categories"><p className="eyebrow">EXPENSES BY CATEGORY · {year}</p>
          {Object.entries(r.expense_categories).map(([c, v]) => <div className="cat-row" key={c}><span>{c}</span><div className="cat-bar"><i style={{ width: `${Math.max(4, (v / t.expenses) * 100)}%` }} /></div><b>{inr(v)}</b></div>)}
        </section>}
        <p className="muted small-note">Profit = collections − refunds − expenses − lab payments (by the date each was recorded).</p>
      </>}
    </div>
  );
}
